import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SortingState } from '@tanstack/react-table';
import { useAuth } from '../../../contexts/AuthContext';
import { apiClient } from '../../../services/api';
import { useToast } from '../../../contexts/ToastContext';
import { AdminOrganization, AdminTenant, AdminTelemetry, Plan } from '../../../types/domain';
import { FilterRule } from '../../../types/grid';
import { getGridSettings, saveGridSettings } from '../../../utils/gridPersistence';
import { useAdminMutations } from './useAdminMutations';

export function useAdminData(activeSection: 'overview' | 'tenants' | 'organizations' | 'queues') {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [organizations, setOrganizations] = useState<AdminOrganization[]>([]);
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [telemetry, setTelemetry] = useState<AdminTelemetry | null>(null);

  // Server-side pagination, search & sort state for organizations
  const [orgsPage, setOrgsPage] = useState(1);
  const [orgsPageSize, setOrgsPageSize] = useState(25);
  const [totalOrgs, setTotalOrgs] = useState(0);
  const [orgsSearch, setOrgsSearch] = useState('');
  const [orgsSortRules, setOrgsSortRules] = useState<SortingState>(() => {
    const saved = getGridSettings('admin_organizations');
    if (saved?.sortRules && saved.sortRules.length > 0) return saved.sortRules;
    if (saved?.sortCol) return [{ id: saved.sortCol, desc: (saved?.sortDir as 'asc' | 'desc') === 'desc' }];
    return [{ id: 'created_at', desc: true }];
  });
  const [orgsFilterRules, setOrgsFilterRules] = useState<FilterRule[]>(() => {
    const saved = getGridSettings('admin_organizations');
    return saved?.filterRules || [];
  });
  const [isOrgsLoading, setIsOrgsLoading] = useState(false);

  // Server-side pagination, search & sort state for tenants
  const [tenantsPage, setTenantsPage] = useState(1);
  const [tenantsPageSize, setTenantsPageSize] = useState(25);
  const [totalTenants, setTotalTenants] = useState(0);
  const [tenantsSearch, setTenantsSearch] = useState('');
  const [tenantsSortRules, setTenantsSortRules] = useState<SortingState>(() => {
    const saved = getGridSettings('admin_tenants');
    if (saved?.sortRules && saved.sortRules.length > 0) return saved.sortRules;
    if (saved?.sortCol) return [{ id: saved.sortCol, desc: (saved?.sortDir as 'asc' | 'desc') === 'desc' }];
    return [{ id: 'created_at', desc: true }];
  });
  const [tenantsFilterRules, setTenantsFilterRules] = useState<FilterRule[]>(() => {
    const saved = getGridSettings('admin_tenants');
    return saved?.filterRules || [];
  });
  const [isTenantsLoading, setIsTenantsLoading] = useState(false);

  // Security Guard: only superadmins can access this view
  useEffect(() => {
    if (user && !user.is_superadmin) {
      showToast({
        type: 'error',
        title: 'Unauthorized Access',
        message: 'Platform administrator permissions are required to access this dashboard.',
      });
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate, showToast]);

  const fetchTelemetry = useCallback(async () => {
    try {
      const telemRes = await apiClient<AdminTelemetry>('/admin/telemetry');
      setTelemetry(telemRes);
    } catch (err) {
      console.warn('Could not fetch admin telemetry from API:', err);
      setTelemetry(null);
    }
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      const plansRes = await apiClient<{ data: Plan[] }>('/plans');
      setPlans(plansRes.data || []);
    } catch (err) {
      console.warn('Could not fetch plans from API:', err);
    }
  }, []);

  const fetchOrganizations = useCallback(
    async (page: number, limit: number, search: string, sortRules: SortingState, filters: FilterRule[]) => {
      setIsOrgsLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', String(limit));
        if (search) params.set('search', search);

        const sortQuery = sortRules && sortRules.length > 0
          ? sortRules.map((s) => `${s.id}:${s.desc ? 'desc' : 'asc'}`).join(',')
          : 'created_at:desc';
        params.set('sort', sortQuery);

        if (filters && filters.length > 0) {
          params.set('filters', JSON.stringify(filters));
        }

        const orgsRes = await apiClient<{ data: AdminOrganization[]; total: number }>(
          `/admin/organizations?${params.toString()}`
        );
        setOrganizations(orgsRes.data || []);
        setTotalOrgs(orgsRes.total || 0);
      } catch (err) {
        console.warn('Could not fetch organizations from API:', err);
        setOrganizations([]);
        setTotalOrgs(0);
      } finally {
        setIsOrgsLoading(false);
      }
    },
    []
  );

  const fetchTenants = useCallback(
    async (page: number, limit: number, search: string, sortRules: SortingState, filters: FilterRule[]) => {
      setIsTenantsLoading(true);
      try {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', String(limit));
        if (search) params.set('search', search);

        const sortQuery = sortRules && sortRules.length > 0
          ? sortRules.map((s) => `${s.id}:${s.desc ? 'desc' : 'asc'}`).join(',')
          : 'created_at:desc';
        params.set('sort', sortQuery);

        if (filters && filters.length > 0) {
          params.set('filters', JSON.stringify(filters));
        }

        const tenantsRes = await apiClient<{ data: AdminTenant[]; total: number }>(
          `/admin/tenants?${params.toString()}`
        );
        setTenants(tenantsRes.data || []);
        setTotalTenants(tenantsRes.total || 0);
      } catch (err) {
        console.warn('Could not fetch tenants from API:', err);
        setTenants([]);
        setTotalTenants(0);
      } finally {
        setIsTenantsLoading(false);
      }
    },
    []
  );

  // Initial loads
  useEffect(() => {
    Promise.all([fetchTelemetry(), fetchPlans()]).finally(() => {
      setIsLoading(false);
    });
  }, [fetchTelemetry, fetchPlans]);

  // Fetch organizations when on organizations tab or overview
  useEffect(() => {
    if (activeSection === 'organizations' || activeSection === 'overview') {
      fetchOrganizations(orgsPage, orgsPageSize, orgsSearch, orgsSortRules, orgsFilterRules);
    }
  }, [fetchOrganizations, activeSection, orgsPage, orgsPageSize, orgsSearch, orgsSortRules, orgsFilterRules]);

  // Fetch tenants when on tenants tab or overview
  useEffect(() => {
    if (activeSection === 'tenants' || activeSection === 'overview') {
      fetchTenants(tenantsPage, tenantsPageSize, tenantsSearch, tenantsSortRules, tenantsFilterRules);
    }
  }, [fetchTenants, activeSection, tenantsPage, tenantsPageSize, tenantsSearch, tenantsSortRules, tenantsFilterRules]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    Promise.all([
      fetchTelemetry(),
      fetchPlans(),
      fetchOrganizations(orgsPage, orgsPageSize, orgsSearch, orgsSortRules, orgsFilterRules),
      fetchTenants(tenantsPage, tenantsPageSize, tenantsSearch, tenantsSortRules, tenantsFilterRules),
    ]).finally(() => {
      setIsRefreshing(false);
    });
  };

  // Organizations grid controls
  const handleOrgsPageChange = useCallback((newPage: number) => setOrgsPage(newPage), []);
  const handleOrgsPageSizeChange = useCallback((newSize: number) => {
    setOrgsPageSize(newSize);
    setOrgsPage(1);
  }, []);
  const handleOrgsSearchChange = useCallback((search: string) => {
    setOrgsSearch((prev) => {
      if (prev === search) return prev;
      setOrgsPage(1);
      return search;
    });
  }, []);
  const handleOrgsSortChange = useCallback((field: string, dir: 'asc' | 'desc' | '', allSorts?: SortingState) => {
    let nextRules: SortingState = [];
    if (allSorts && allSorts.length > 0) {
      nextRules = allSorts;
    } else if (field && dir) {
      nextRules = [{ id: field, desc: dir === 'desc' }];
    } else {
      nextRules = [{ id: 'created_at', desc: true }];
    }
    setOrgsSortRules(nextRules);
    setOrgsPage(1);
    saveGridSettings('admin_organizations', {
      sortRules: nextRules,
      sortCol: nextRules[0]?.id || 'created_at',
      sortDir: nextRules[0]?.desc ? 'desc' : 'asc',
    });
  }, []);
  const handleOrgsFilterChange = useCallback((rules: FilterRule[]) => {
    setOrgsFilterRules(rules);
    setOrgsPage(1);
    saveGridSettings('admin_organizations', { filterRules: rules });
  }, []);

  // Tenants grid controls
  const handleTenantsPageChange = useCallback((newPage: number) => setTenantsPage(newPage), []);
  const handleTenantsPageSizeChange = useCallback((newSize: number) => {
    setTenantsPageSize(newSize);
    setTenantsPage(1);
  }, []);
  const handleTenantsSearchChange = useCallback((search: string) => {
    setTenantsSearch((prev) => {
      if (prev === search) return prev;
      setTenantsPage(1);
      return search;
    });
  }, []);
  const handleTenantsSortChange = useCallback((field: string, dir: 'asc' | 'desc' | '', allSorts?: SortingState) => {
    let nextRules: SortingState = [];
    if (allSorts && allSorts.length > 0) {
      nextRules = allSorts;
    } else if (field && dir) {
      nextRules = [{ id: field, desc: dir === 'desc' }];
    } else {
      nextRules = [{ id: 'created_at', desc: true }];
    }
    setTenantsSortRules(nextRules);
    setTenantsPage(1);
    saveGridSettings('admin_tenants', {
      sortRules: nextRules,
      sortCol: nextRules[0]?.id || 'created_at',
      sortDir: nextRules[0]?.desc ? 'desc' : 'asc',
    });
  }, []);
  const handleTenantsFilterChange = useCallback((rules: FilterRule[]) => {
    setTenantsFilterRules(rules);
    setTenantsPage(1);
    saveGridSettings('admin_tenants', { filterRules: rules });
  }, []);

  const {
    updatingOrgId,
    handleTierUpgrade,
    handleUpdateOrg,
    handleDeleteOrg,
    handleBulkDeleteOrgs,
    handleDeleteTenant,
    handleBulkDeleteTenants,
  } = useAdminMutations({
    plans,
    setOrganizations,
    refreshOrganizations: () =>
      fetchOrganizations(orgsPage, orgsPageSize, orgsSearch, orgsSortRules, orgsFilterRules),
    refreshTenants: () =>
      fetchTenants(tenantsPage, tenantsPageSize, tenantsSearch, tenantsSortRules, tenantsFilterRules),
    refreshTelemetry: fetchTelemetry,
  });

  return {
    user,
    isLoading,
    isRefreshing,
    organizations,
    tenants,
    plans,
    telemetry,
    updatingOrgId,
    orgsPage,
    orgsPageSize,
    totalOrgs,
    orgsSearch,
    orgsSortRules,
    orgsFilterRules,
    isOrgsLoading,
    tenantsPage,
    tenantsPageSize,
    totalTenants,
    tenantsSearch,
    tenantsSortRules,
    tenantsFilterRules,
    isTenantsLoading,
    handleRefresh,
    handleOrgsPageChange,
    handleOrgsPageSizeChange,
    handleOrgsSearchChange,
    handleOrgsSortChange,
    handleOrgsFilterChange,
    handleTenantsPageChange,
    handleTenantsPageSizeChange,
    handleTenantsSearchChange,
    handleTenantsSortChange,
    handleTenantsFilterChange,
    handleTierUpgrade,
    handleUpdateOrg,
    handleDeleteOrg,
    handleBulkDeleteOrgs,
    handleDeleteTenant,
    handleBulkDeleteTenants,
  };
}
